import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag, Button, Input, Row, Col, DatePicker, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useGetTripsHistoryByRiderIdQuery } from '../../../../GraphQL/hooks/useGetTripsHistoryByRiderIdQuery';
import dayjs, { Dayjs } from 'dayjs';

import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

const { RangePicker } = DatePicker;

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface Trip {
    reference: string;
    date: string;
    driverName: string;
    status: string;
    document?: string;
    rawDate?: number;
}

const statusColors: Record<string, string> = {
    Completed: 'green',
    Canceled: 'red',
    Closed: 'green',
    default: 'blue',
};

interface TripsHistoryProps {
    riderId: string;
    open: boolean;
    handleViewInvoice?: (trip: Trip) => void;
}

export const TripsHistory: React.FC<TripsHistoryProps> = ({ riderId, open, handleViewInvoice }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);

    useEffect(() => {
        if (!open) {
            setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
            setSearchText('');
            setCurrentPage(1);
        }
    }, [open]);

    const pageSize = 10;
    const { data: tripsData, loading } = useGetTripsHistoryByRiderIdQuery({
        id: riderId,
        invoiceType: 'taxi_mobility',
        page: currentPage,
        limit: pageSize,
    });

    // Map trips and filter by search and date range
    const tripsHistory: Trip[] = useMemo(() => {
        const trips = tripsData?.data ?? [];
        const mappedTrips: Trip[] = trips.map((trip: any, index: number) => {
            const timestamp = Number(trip.date);
            const formattedDate = !isNaN(timestamp) ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '';
            return {
                reference: trip.reference ?? `trip-${index}`,
                date: formattedDate,
                driverName: trip.taxiDriverFullName,
                status: trip.status,
                document: trip.invoiceAttachment?.url,
                rawDate: timestamp,
            };
        });
        return mappedTrips.filter((trip) => {
            const matchesSearch =
                trip.reference.toLowerCase().includes(searchText.toLowerCase()) ||
                trip.driverName?.toLowerCase().includes(searchText.toLowerCase());

            const inDateRange = !trip.rawDate || dayjs(trip.rawDate).isBetween(dateRange[0], dateRange[1], 'day', '[]');

            return matchesSearch && inDateRange;
        });
    }, [tripsData, searchText, dateRange]);

    const handleView = (trip: Trip) => {
        if (trip.document) {
            window.open(trip.document, '_blank', 'noopener,noreferrer');
        } else {
            alert('Document not available');
        }
    };

    const columns: ColumnsType<Trip> = [
        { title: 'Trip ID', dataIndex: 'reference', key: 'reference' },
        {
            title: 'Date',
            dataIndex: 'rawDate',
            key: 'date',
            render: (rawDate: number | undefined) => (rawDate ? dayjs(rawDate).format('YYYY-MM-DD HH:mm') : ''),
        },
        { title: 'Driver', dataIndex: 'driverName', key: 'driverName' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={statusColors[status] || 'default'}>{status}</Tag>,
        },
        {
            title: 'View',
            key: 'view',
            align: 'center',
            render: (_, record) => (
                <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
            ),
        },
    ];

    return (
        <div style={{ flex: '1 1 auto' }}>
            <Row gutter={[15, 15]} style={{ marginBottom: 15 }}>
                <Col>
                    <Input
                        placeholder="Search by Trip ID or Driver Name"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        addonBefore={<SearchOutlined />}
                        style={{ width: 270 }}
                    />
                </Col>
                <Col>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => dates && setDateRange(dates as [Dayjs, Dayjs])}
                    />
                </Col>
            </Row>

            <Spin spinning={loading}>
                <Table<Trip>
                    dataSource={tripsHistory}
                    columns={columns}
                    rowKey="id"
                    bordered
                    size="middle"
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: tripsData?.pagination?.totalCount ?? 0,
                        onChange: (page) => setCurrentPage(page),
                        showTotal: (total) => `Total Trips: ${total}`,
                    }}
                />
            </Spin>
        </div>
    );
};

export default TripsHistory;
